import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import { configurationService } from '../../services/configurationService';
import { ApiError } from '../../services/apiClient';

const CATEGORY = 'Notifications';

const KEYS = {
  enabled: 'Reports.DigestEnabled',
  cadence: 'Reports.DigestCadence',
  weekStart: 'Reports.DigestWeekStartsOn',
  sendHour: 'Reports.DigestSendHourLocal',
  recipients: 'Reports.DigestRecipients',
  allowlist: 'Reports.DigestRecipientDomainAllowlist',
  threshold: 'Reports.LowScoreThreshold',
} as const;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * Field labels for the missing-settings warning.
 *
 * A warning saying "5 of 7 settings are missing" tells a reader that something on screen is
 * untrustworthy without saying what — so the digest switch still shows a confident "off" and there
 * is no way to learn whether it is one of the five. Naming them is what makes the notice actionable.
 */
const KEY_LABELS: Record<string, string> = {
  [KEYS.enabled]: 'Send a scheduled digest',
  [KEYS.cadence]: 'Cadence',
  [KEYS.weekStart]: 'Week starts on',
  [KEYS.sendHour]: 'Send hour',
  [KEYS.recipients]: 'Recipients',
  [KEYS.allowlist]: 'Recipient domain allowlist',
  [KEYS.threshold]: 'Low-score threshold',
};

/**
 * The agency's scheduled QA digest.
 *
 * Deliberately explicit that recipients are restricted and that this mails personnel-adjacent data
 * outside the product — those are the two things a person setting this up needs to understand
 * before typing an address, and neither is obvious from a text field.
 */
export const DigestSettings: React.FC = () => {
  const [values, setValues] = useState<Record<string, string>>({});

  // What the load actually returned, kept separately so "what is stored" and "what is on screen"
  // stay distinguishable. Everything below turns on that distinction.
  const [loadedValues, setLoadedValues] = useState<Record<string, string>>({});
  const [loadFailed, setLoadFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Same data-loading shape as RetentionSettings; the react-hooks/set-state-in-effect advisory is
  // unresolved repo-wide (BACKLOG) and this follows the established pattern rather than diverging.
  const loadSettings = useCallback(async () => {
    try {
      const configs = await configurationService.getConfigurationsByCategory(CATEGORY);
      const loaded = Object.fromEntries(configs.map((c) => [c.configKey, c.configValue ?? '']));
      setValues(loaded);
      setLoadedValues(loaded);
      setLoadFailed(false);
      setError('');
    } catch (_err) {
      setLoadFailed(true);
      setError('Digest settings could not be read. Save is unavailable until they load — use Reset to retry.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const set = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Keys whose value on screen differs from the value that was read.
   *
   * A key absent from the load is submitted only once the user types into it — otherwise a partial
   * read would write it back as an empty string, which is how a key stored under a drifted category
   * silently emptied the QA distribution list. A key that WAS read is submitted only if it moved,
   * which is the same rule the backend now applies to audit records: a form that posts every field
   * on every save wrote seven durable rows for one real change (Sprint 12 Phase 4). Belt and braces
   * — the server drops the no-ops now — but the browser should not send them either.
   *
   * Note a key that drifted to another category and is then edited comes back to this one, because
   * the write carries `CATEGORY`. That is self-healing and wanted here; it is also silent.
   */
  // Trimmed on BOTH sides, because the submitted value is trimmed and `loadedValues` is updated
  // with the trimmed value after a save. Comparing untrimmed left the form permanently dirty after
  // any edit with stray whitespace — normal in a multiline recipients box — so every later Save
  // resubmitted the same key and reported success for a write that changed nothing. What this gives
  // up is a normalize-only save; nothing downstream needs it, since the enum keys parse through
  // `Enum.TryParse` and the numeric ones allow surrounding whitespace.
  const changedKeys = Object.values(KEYS).filter(
    (configKey) => (values[configKey] ?? '').trim() !== (loadedValues[configKey] ?? '').trim()
  );

  // Keys this screen never received. Their inputs show a coded fallback, so the screen must say so
  // rather than presenting a default as the agency's stored configuration — the switch is the sharp
  // edge: rendered `false`, it reads "digest off" for a digest that may be sending nightly.
  const missingKeys = Object.values(KEYS).filter((configKey) => !(configKey in loadedValues));

  const handleSave = async () => {
    try {
      setSaving(true);

      // Only what actually moved. It used to submit all seven unconditionally as
      // `(values[key] ?? '').trim()`, so any key missing from the GET was written as an empty
      // string — and a key stored under a category other than this one IS missing from the GET.
      // That silently cleared `Reports.DigestRecipients`, which has no write-time validator to
      // refuse an empty value: the QA digest distribution list emptied with nothing said. The
      // all-empty case was already caught by the batch validator (the threshold and send-hour keys
      // refuse ''), which is why only the partial case was reachable. Raised by backend-reviewer on
      // the Sprint 12 Phase 4 diff.
      const configurations = changedKeys.map((configKey) => ({
        configKey,
        configValue: (values[configKey] ?? '').trim(),
        category: CATEGORY,
      }));

      if (configurations.length === 0) {
        // Not an error: the user did nothing wrong and nothing failed. A red banner for "nothing
        // happened" is how people are trained to stop reading red banners.
        setSuccess('No changes to save.');
        setTimeout(() => setSuccess(''), 4000);
        return;
      }

      await configurationService.updateMultipleConfigurations({ configurations });
      // The saved values are now the stored values, so a second Save is correctly a no-op rather
      // than resubmitting the same keys.
      setLoadedValues((prev) => ({
        ...prev,
        ...Object.fromEntries(configurations.map((c) => [c.configKey, c.configValue])),
      }));
      setSuccess('Digest schedule updated.');
      setError('');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      // Surface the server's own message — it names the legal range and why.
      setError((err as ApiError)?.message || 'Failed to update digest settings.');
      setSuccess('');
    } finally {
      setSaving(false);
    }
  };

  const enabled = (values[KEYS.enabled] ?? 'false').toLowerCase() === 'true';
  const cadence = values[KEYS.cadence] || 'Daily';
  const hour = Number((values[KEYS.sendHour] ?? '7').trim());
  const threshold = Number((values[KEYS.threshold] ?? '70').trim());
  const hourValid = Number.isInteger(hour) && hour >= 0 && hour <= 23;
  const thresholdValid = Number.isFinite(threshold) && threshold >= 0 && threshold <= 100;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress aria-label="Loading digest settings" />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        Scheduled QA Digest
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>This emails operator names and their scores outside the product</AlertTitle>
        A recipient must already be a provisioned user of this agency, or be on the domain
        allowlist below. Anything else is refused rather than delivered — an unverified address is
        a disclosure, not a delivery problem.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/*
        A value this screen did not read is not a value it may present as the agency's. Every field
        falls back to a coded default when its key is absent — and the switch is the sharp edge: it
        renders `false`, which reads "the digest is off" for a digest that may be sending nightly,
        directly under a banner about operator names leaving the product. A key written with a blank
        category lands under General and disappears from this tab, which is how that happens. Found
        by design-reviewer as HIGH, 2026-08-04.
      */}
      {!loadFailed && missingKeys.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Some settings were not found for this agency</AlertTitle>
          Not found: {missingKeys.map((k) => KEY_LABELS[k] ?? k).join(', ')}. Those fields show
          defaults, which is accurate if the setting was never configured — but a setting stored
          under a different category is also invisible here, and then the value shown is not the one
          in force. Saving writes only the fields you change, and brings any you change back onto
          this tab.
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => set(KEYS.enabled, String(e.target.checked))}
                />
              }
              label="Send a scheduled digest to this agency"
            />

            <TextField
              select
              label="Cadence"
              value={cadence}
              onChange={(e) => set(KEYS.cadence, e.target.value)}
              helperText="Match this to how often calls are ingested. A nightly SFTP drop wants Daily, so a low-scoring call reaches a supervisor the next morning rather than up to a week later."
              sx={{ maxWidth: 420 }}
            >
              <MenuItem value="Daily">Daily</MenuItem>
              <MenuItem value="Weekly">Weekly</MenuItem>
            </TextField>

            {cadence === 'Weekly' && (
              <TextField
                select
                label="Week starts on"
                value={values[KEYS.weekStart] || 'Monday'}
                onChange={(e) => set(KEYS.weekStart, e.target.value)}
                sx={{ maxWidth: 420 }}
              >
                {DAYS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              label="Send at (hour, agency local time)"
              type="number"
              value={values[KEYS.sendHour] ?? '7'}
              onChange={(e) => set(KEYS.sendHour, e.target.value)}
              inputProps={{ min: 0, max: 23 }}
              error={!hourValid}
              helperText={
                hourValid
                  ? 'Sent at or after this hour, once the period has closed. 7 means 07:00 at the agency.'
                  : 'Enter a whole number between 0 and 23.'
              }
              sx={{ maxWidth: 420 }}
            />

            <TextField
              label="Low-score threshold"
              type="number"
              value={values[KEYS.threshold] ?? '70'}
              onChange={(e) => set(KEYS.threshold, e.target.value)}
              inputProps={{ min: 0, max: 100, step: 1 }}
              error={!thresholdValid}
              helperText={
                thresholdValid
                  ? 'Calls scoring at or below this are listed in the digest. Scores are a weighted percentage. Set this to your own acceptance bar — 70 is only a starting point.'
                  : 'Enter a number between 0 and 100.'
              }
              sx={{ maxWidth: 420 }}
            />

            <TextField
              label="Recipients"
              value={values[KEYS.recipients] ?? ''}
              onChange={(e) => set(KEYS.recipients, e.target.value)}
              multiline
              minRows={2}
              helperText="Semicolon- or comma-separated. Each must be a provisioned user of this agency, or on the allowlist below."
              fullWidth
            />

            <TextField
              label="Additional allowed mail domains"
              value={values[KEYS.allowlist] ?? ''}
              onChange={(e) => set(KEYS.allowlist, e.target.value)}
              helperText="For a chief or accreditation manager who never signs in, e.g. cityname.gov. Matched on the exact domain, not a suffix. Leave empty to allow no exceptions."
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              {/*
                Blocked when the read failed. The whole safety model of the filter above is "values
                reflects what we read" — when the read failed nothing on screen is a read value, so
                every field shows a coded default and a save would be made on a false premise. The
                empty-batch branch in handleSave is the backstop, not the defence. Reset retries.
              */}
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || loadFailed || !hourValid || !thresholdValid}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setLoading(true);
                  loadSettings();
                }}
                disabled={saving}
              >
                Reset
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
